class InstagramVideosChroxel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.prevButton = this.querySelector('[data-prev]');
    this.nextButton = this.querySelector('[data-next]');
    this.originalCount = 0;

    this.setupLoop();

    this.prevButton?.addEventListener('click', () => {
      this.scrollByPage(-1);
      this.restartAutoplay();
    });
    this.nextButton?.addEventListener('click', () => {
      this.scrollByPage(1);
      this.restartAutoplay();
    });
    // Seamless wrap: past the end of the originals we're looking at clones,
    // so jumping back one period lands on an identical frame.
    this.track?.addEventListener('scroll', () => this.wrap(), { passive: true });

    this.addEventListener('click', (event) => {
      const playButton = event.target.closest('.instagram-videos-chroxel__play');
      if (playButton) this.playCard(playButton.closest('.instagram-videos-chroxel__card'));
    });

    this.autoplayEnabled =
      this.dataset.autoplay === 'true' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplaySpeed = (parseFloat(this.dataset.speed) || 5) * 1000;
    this.autoplayTimer = null;

    if (this.autoplayEnabled && this.track) {
      // Pause while the user is interacting with the carousel
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay());
      this.addEventListener('focusin', () => this.stopAutoplay());
      this.addEventListener('focusout', () => this.startAutoplay());
      this.track.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
      this.track.addEventListener('touchend', () => this.startAutoplay(), { passive: true });
      document.addEventListener(
        'visibilitychange',
        (this.onVisibility = () => (document.hidden ? this.stopAutoplay() : this.startAutoplay()))
      );
      this.startAutoplay();
    }
  }

  disconnectedCallback() {
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    this.stopAutoplay();
  }

  setupLoop() {
    if (!this.track) return;
    const items = Array.from(this.track.children);
    if (items.length < 2) return;
    this.originalCount = items.length;
    items.forEach((item) => {
      const clone = item.cloneNode(true);
      clone.dataset.clone = 'true';
      this.track.append(clone);
    });
  }

  metrics() {
    const item = this.track.querySelector('.instagram-videos-chroxel__item');
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = item ? item.offsetWidth + gap : this.track.clientWidth;
    const perPage = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));
    return { step, perPage, period: step * this.originalCount };
  }

  wrap() {
    if (!this.originalCount) return;
    const { period } = this.metrics();
    if (this.track.scrollLeft >= period) this.track.scrollLeft -= period;
  }

  scrollByPage(direction) {
    if (!this.track) return;
    const { step, perPage, period } = this.metrics();

    // Going backwards from the start: jump (instantly, same frame) into the
    // cloned copy so there is room to keep scrolling left.
    if (direction < 0 && this.originalCount && this.track.scrollLeft < step * perPage) {
      this.track.scrollLeft += period;
    }

    this.track.scrollBy({ left: direction * step * perPage, behavior: 'smooth' });
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.autoplayTimer) return;
    this.autoplayTimer = setInterval(() => this.autoAdvance(), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  autoAdvance() {
    if (!this.track) return;
    // Don't steal the show while someone is watching a video
    const watching = Array.from(this.querySelectorAll('video')).some((video) => !video.paused);
    if (watching) return;
    this.scrollByPage(1);
  }

  playCard(card) {
    if (!card) return;
    const videoUrl = card.dataset.videoUrl;

    // The CDN URL expires between syncs; fall back to opening the reel on Instagram.
    if (!videoUrl) {
      this.openPermalink(card);
      return;
    }

    this.pauseOthers(card);

    let video = card.querySelector('video');
    if (video) {
      video.paused ? video.play() : video.pause();
      return;
    }

    video = document.createElement('video');
    video.className = 'instagram-videos-chroxel__video';
    video.src = videoUrl;
    video.playsInline = true;
    video.controls = true;
    video.preload = 'metadata';

    video.addEventListener('error', () => {
      video.remove();
      card.classList.remove('is-playing');
      delete card.dataset.videoUrl;
      this.openPermalink(card);
    });

    video.addEventListener('play', () => this.pauseOthers(card));

    card.append(video);
    card.classList.add('is-playing');
    video.play().catch(() => {});
  }

  pauseOthers(activeCard) {
    this.querySelectorAll('video').forEach((video) => {
      if (!activeCard.contains(video)) video.pause();
    });
  }

  openPermalink(card) {
    if (card.dataset.permalink) window.open(card.dataset.permalink, '_blank', 'noopener');
  }
}

customElements.define('instagram-videos-chroxel', InstagramVideosChroxel);
